import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { displayApolloError } from '@optools/components/notifications/apollo';
import { Button } from '@optools/components/ui/button';
import { BTC_GAME_FRAGMENT } from '@optools/services/operator-gateway/btc/fragments';
import {
  ARCHIVE_MEDIA,
  START_MEDIA,
  STOP_MEDIA,
  UNSCHEDULE_MEDIA,
} from '@optools/services/operator-gateway/btc/mutations';
import { PipelineStatus } from '@optools/services/operator-gateway/types/generated/graphql';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';

export interface PipelineControlButtonProps {
  gamePk: number;
  mediaId: string;
  pipelineId: number;
  status: PipelineStatus;
}

export function PipelineControlButton({
  gamePk,
  mediaId,
  pipelineId,
  status,
}: PipelineControlButtonProps) {
  const navigate = useNavigate();

  const [unscheduleMedia, unscheduleMediaStatus] = useMutation(UNSCHEDULE_MEDIA, {
    onCompleted: ({ unscheduleMedia }) => {
      const [{ mediaId, reason, success }] = unscheduleMedia;
      if (success) {
        toast.success('Unschedule Media Success', { description: `Media ID: ${mediaId}` });
        navigate({
          params: { gamePk },
          search: (params) => ({ date: params.date }),
          to: '/btc-tools/schedule/$gamePk/pipelines',
        });
      } else {
        toast.error('Unschedule Media Error', {
          description: (
            <>
              <h2>Media ID: {mediaId}</h2>
              <p>{reason}</p>
            </>
          ),
          duration: Infinity,
        });
      }
    },
    onError: (error) => {
      return displayApolloError({
        error,
        options: { description: `Media ID: ${mediaId}` },
        title: 'Unschedule Media Error',
      });
    },
    update: (cache, { data }) => {
      if (data?.unscheduleMedia.length) {
        const [{ success }] = data.unscheduleMedia;
        if (success) {
          const game = cache.readFragment({
            fragment: BTC_GAME_FRAGMENT,
            fragmentName: 'BTCGameFields',
            id: cache.identify({ __typename: 'Game', gamePk }),
          });

          if (game) {
            cache.writeFragment({
              data: {
                ...game,
                pipelines: game.pipelines.filter((pipeline) => pipeline.id !== pipelineId),
              },
              fragment: BTC_GAME_FRAGMENT,
              fragmentName: 'BTCGameFields',
              id: cache.identify({ __typename: 'Game', gamePk }),
            });

            cache.evict({ id: cache.identify({ __typename: 'MediaPipeline', id: pipelineId }) });
            cache.gc();
          }
        }
      }
    },
  });

  const [startMedia, startMediaStatus] = useMutation(START_MEDIA, {
    onCompleted: ({ startMedia }) => {
      const [{ mediaId, reason, success }] = startMedia;
      if (success) {
        toast.success('Start Media Success', { description: `Media ID: ${mediaId}` });
      } else {
        toast.error('Start Media Error', {
          description: (
            <>
              <h2>Media ID: {mediaId}</h2>
              <p>{reason}</p>
            </>
          ),
          duration: Infinity,
        });
      }
    },
    onError: (error) =>
      displayApolloError({
        error,
        options: { description: `Media ID: ${mediaId}` },
        title: 'Start Media Error',
      }),
  });

  const [stopMedia, stopMediaStatus] = useMutation(STOP_MEDIA, {
    onCompleted: ({ stopMedia }) => {
      const [{ mediaId, reason, success }] = stopMedia;
      if (success) {
        toast.success('Stop Media Success', { description: `Media ID: ${mediaId}` });
      } else {
        toast.error('Stop Media Error', {
          description: (
            <>
              <h2>Media ID: {mediaId}</h2>
              <p>{reason}</p>
            </>
          ),
          duration: Infinity,
        });
      }
    },
    onError: (error) =>
      displayApolloError({
        error,
        options: { description: `Media ID: ${mediaId}` },
        title: 'Stop Media Error',
      }),
  });

  const [archiveMedia, archiveMediaStatus] = useMutation(ARCHIVE_MEDIA, {
    onCompleted: ({ archiveMedia }) => {
      const [{ mediaId, reason, success }] = archiveMedia;
      if (success) {
        toast.success('Archive Media Success', { description: `Media ID: ${mediaId}` });
      } else {
        toast.error('Archive Media Error', {
          description: (
            <>
              <h2>Media ID: {mediaId}</h2>
              <p>{reason}</p>
            </>
          ),
          duration: Infinity,
        });
      }
    },
    onError: (error) =>
      displayApolloError({
        error,
        options: { description: `Media ID: ${mediaId}` },
        title: 'Archive Media Error',
      }),
  });

  return [
    PipelineStatus.PROVISIONED,
    PipelineStatus.STARTING,
    PipelineStatus.SCHEDULED,
    PipelineStatus.RUNNING,
    PipelineStatus.ENDING,
    PipelineStatus.ENDED,
  ].includes(status) ? (
    <div className="flex flex-col gap-2">
      <div className="flex gap-2">
        {[PipelineStatus.PROVISIONED, PipelineStatus.STARTING, PipelineStatus.SCHEDULED].includes(
          status,
        ) ? (
          <ConfirmationDialog
            description="Are you sure you want to deprovision this pipeline?"
            title="Deprovision Pipeline"
            onConfirm={() => unscheduleMedia({ variables: { mediaIds: [mediaId] } })}>
            <Button
              aria-label="Deprovision"
              className="w-30"
              disabled={unscheduleMediaStatus.loading || status === PipelineStatus.STARTING}
              loading={unscheduleMediaStatus.loading}
              variant="outline">
              Deprovision
            </Button>
          </ConfirmationDialog>
        ) : null}

        {/* Start Stream */}
        {[PipelineStatus.PROVISIONED, PipelineStatus.STARTING].includes(status) ? (
          <ConfirmationDialog
            description="Are you sure you want to start the stream?"
            title="Start Stream"
            onConfirm={() => startMedia({ variables: { mediaIds: [mediaId] } })}>
            <Button
              aria-label="Start Stream"
              className="w-30"
              disabled={startMediaStatus.loading || status === PipelineStatus.STARTING}
              loading={startMediaStatus.loading || status === PipelineStatus.STARTING}>
              Start Stream
            </Button>
          </ConfirmationDialog>
        ) : null}

        {/* End Stream */}
        {[PipelineStatus.RUNNING, PipelineStatus.ENDING].includes(status) ? (
          <ConfirmationDialog
            description="Are you sure you want to end the stream?"
            title="End Stream"
            onConfirm={() => stopMedia({ variables: { mediaIds: [mediaId] } })}>
            <Button
              aria-label="End Stream"
              className="w-30"
              disabled={stopMediaStatus.loading || status === PipelineStatus.ENDING}
              loading={stopMediaStatus.loading || status === PipelineStatus.ENDING}
              variant="destructive">
              End Stream
            </Button>
          </ConfirmationDialog>
        ) : null}

        {/* Archive Stream */}
        {[PipelineStatus.ENDED].includes(status) ? (
          <ConfirmationDialog
            description="Are you sure you want to archive the stream?"
            title="Archive Stream"
            onConfirm={() => archiveMedia({ variables: { mediaIds: [mediaId] } })}>
            <Button
              aria-label="Archive Stream"
              className="w-30"
              disabled={archiveMediaStatus.loading}
              loading={archiveMediaStatus.loading}
              variant="secondary">
              Archive Stream
            </Button>
          </ConfirmationDialog>
        ) : null}
      </div>
    </div>
  ) : null;
}
